/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import { ICoreContext } from '@msdyn365-commerce/core-internal';
import { CartLine } from '@msdyn365-commerce/retail-proxy';
import * as React from 'react';
import { ICheckoutViewProps, ILineItem, ILineItemDeliveryGroup, ILineItems, IOrderSummary, IPickUpAtStore, ISmweCheckoutData } from '../../../modules/smwe-checkout';
import { brandCodeParser } from '../../../Utilities/brand-code-parser';
import { ISmweCheckoutProps } from '../definition-extensions/smwe-checkout.ext.props.autogenerated';

export const PickUpAtStoreComponent: React.FC<IPickUpAtStore> = ({ PickUpAtStore, label, location }) => (
    <Node {...PickUpAtStore}>
        {label}
        {location}
    </Node>
);

export const LineItemComponent: React.FC<{ line: ILineItem; cartItem?: CartLine; context: ICoreContext }> = ({ line: { LineItem, item, pickUpAtStore }, cartItem, context }) => {
    let brandCode: string = '';
    if (cartItem) {
        brandCode = brandCodeParser({ description: cartItem.Description, appContext: context });
    }
    return (
        <Node {...LineItem}>
            {brandCode && <div className='msc-cart-line__brand'>{brandCode}</div>}
            {item}
            {pickUpAtStore && <PickUpAtStoreComponent {...pickUpAtStore} />}
        </Node>
    );
};

export const LineItemGroupComponent: React.FC<{ group: ILineItemDeliveryGroup; props: ISmweCheckoutProps<{}>; data: ISmweCheckoutData; context: ICoreContext }> = (
    { group: { LineItemDeliveryGroup, LineItemList, heading, lineItems }, props, data, context }
) => {
    const { nonSubscriptionHeader } = props.resources;
    return (
        <Node {...LineItemDeliveryGroup}>
            {heading}
            {lineItems.length > 0 &&
                <Node {...LineItemList}>
                    <div className='ms-checkout__line-items-list-title'>{nonSubscriptionHeader}</div>
                    {lineItems.map(lineItem => {
                        // get the item and send it to the component
                        const item = data.checkout.result?.checkoutCart.cart.CartLines?.find(cartItem => cartItem.LineId === lineItem.LineId);
                        return (
                            <LineItemComponent key={lineItem.LineId} line={lineItem} cartItem={item} context={context} />
                        );
                    })}
                </Node>
            }
        </Node>
    );
};

export const LineItemsComponent: React.FC<{ lineItems: ILineItems; props: ISmweCheckoutProps<{}>; data: ISmweCheckoutData; context: ICoreContext }> = (
    { lineItems: { LineItems, Header, heading, editLink, itemsForPickup, itemsForShip, itemsForSubscription, subscriptionHeader },
    props,
    data,
    context }
) => (
    <Node {...LineItems}>
        <Node {...Header}>
            {heading}
            {editLink}
        </Node>
        {itemsForPickup && <LineItemGroupComponent group={itemsForPickup} props={props} data={data} context={context} />}
        {itemsForShip && <LineItemGroupComponent group={itemsForShip} props={props} data={data} context={context} />}
        {itemsForSubscription && itemsForSubscription.length > 0 &&
            <Node className='ms-checkout__line-items-list'>
                <div className='ms-checkout__line-items-list-title'>{subscriptionHeader}</div>
                {itemsForSubscription.map((lineItem: ILineItem) => {
                    // get the item and send it to the component
                    const item = data.checkout.result?.checkoutCart.cart.CartLines?.find(cartItem => cartItem.LineId === lineItem.LineId);
                    return (
                        <LineItemComponent key={lineItem.LineId} line={lineItem} cartItem={item} context={context} />
                    );
                })}
            </Node>
        }
    </Node>
);

const OrderSummaryComponent: React.FC<IOrderSummary> = ({ heading, lines }) => (
    <div className='msc-order-summary-wrapper'>
        {heading}
        <div className='msc-order-summary__items'>
            {lines && (
                <>
                    {lines.subtotal}
                    {lines.shipping}
                    {lines.tax}
                    {lines.totalDiscounts}
                    {lines.loyalty}
                    {lines.giftCard}
                    {lines.orderTotal}
                </>
            )}
        </div>
    </div>
);

const CheckoutView: React.FC<ICheckoutViewProps & ISmweCheckoutProps<{}>> = props => {
    const {
        canShow,
        checkoutProps,
        mainProps,
        mainControlProps,
        sideProps,
        loading,
        alert,
        guidedForm,
        orderSummary,
        lineItems,
        placeOrderButton,
        data,
        context
    } = props;
    return (
        <Module {...checkoutProps}>
            {loading}
            {alert}
            {canShow && (
                <>
                    <Node {...mainProps}>
                        {guidedForm}
                        <Node {...mainControlProps}>
                            {placeOrderButton}
                        </Node>
                    </Node>
                    <Node {...sideProps}>
                        {orderSummary && <OrderSummaryComponent {...orderSummary} />}
                        {lineItems && <LineItemsComponent lineItems={lineItems} props={props} data={data} context={context} />}
                    </Node>
                </>
            )}
        </Module>
    );
};

export default CheckoutView;
